import { useState, memo, useCallback, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';
import { MoreHorizontal, FolderPlus, Trash2, Share2, ShoppingBag, Check, Pin, CheckCircle2, Circle, FolderCheck, Shirt, Vote, MessageCircle, AlertTriangle, Wrench } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useLongPress } from '@/hooks/useLongPress';
import type { Product } from '@/types/product';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AddToCollectionSheet } from '@/components/collections/AddToCollectionSheet';
import { ShareSheet } from '@/components/share/ShareSheet';
import { CreatePollSheet } from '@/components/polls/CreatePollSheet';
import { getPlatformFromUrl } from '@/components/products/SocialPlatformBadge';
import { isFashionCategory } from '@/hooks/useCompleteTheLook';
import { usePurchases } from '@/hooks/usePurchases';
import { useSavedItems } from '@/hooks/useSavedItems';
import { useAuth } from '@/hooks/useAuth';
import { useRegionalSettings } from '@/hooks/useRegionalSettings';
import { toast } from 'sonner';
import { FashionLoadingAnimation } from './FashionLoadingAnimation';
import { TryOnNudgeTooltip } from './TryOnNudgeTooltip';
import { FixItemInfoSheet } from './FixItemInfoSheet';

interface SavesGridCardProps {
  product: Product & { 
    url?: string; 
    isSavedItem?: boolean; 
    sourceType?: string; 
    price: number | null;
    focalPoint?: { x: number; y: number } | null;
    pinned_at?: string | null;
    isExtracting?: boolean;
    ai_category?: string | null;
  };
  onDelete?: (id: string) => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  onLongPressSelect?: (id: string) => void;
  isInCollection?: boolean;
  priority?: boolean;
  showTapHint?: boolean;
  onTapHintDismiss?: () => void;
  showTryOnNudge?: boolean;
  onTryOnNudgeDismiss?: () => void;
  isReported?: boolean;
}

/**
 * Simplified grid card for the saves page - matches the search grid design.
 * Square image, price pill overlay, clean store/name text below.
 */
export const SavesGridCard = memo(function SavesGridCard({
  product,
  onDelete,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
  onLongPressSelect,
  isInCollection = false,
  priority = false,
  showTapHint = false,
  onTapHintDismiss,
  showTryOnNudge = false,
  onTryOnNudgeDismiss,
  isReported = false,
}: SavesGridCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isItemPurchased, markAsPurchased } = usePurchases();
  const { togglePin } = useSavedItems();
  const { formatPrice, loading: ratesLoading } = useRegionalSettings();
  const [showAddToCollection, setShowAddToCollection] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [showCreatePoll, setShowCreatePoll] = useState(false);
  const [showFixInfo, setShowFixInfo] = useState(false);
  const [isLongPressing, setIsLongPressing] = useState(false);
  
  const socialPlatform = getPlatformFromUrl(product.url);
  const linkTo = `/saved/${product.id}`;
  const hasValidPrice = product.price != null && product.price > 0;
  const isPurchased = isItemPurchased(product.id);
  const isPinned = !!product.pinned_at;
  const isExtracting = !!product.isExtracting;

  const formattedPrice = useMemo(() => {
    if (!hasValidPrice || ratesLoading) return null;
    return formatPrice(product.price, product.currency);
  }, [hasValidPrice, ratesLoading, formatPrice, product.price, product.currency]);

  const handleTogglePin = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    togglePin.mutate({ itemId: product.id, isPinned }, {
      onSuccess: () => toast.success(isPinned ? 'Unpinned' : 'Pinned to top'),
      onError: () => toast.error('Failed to update')
    });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.(product.id);
  };

  const handleLongPressAction = useCallback(() => {
    setIsLongPressing(true);
    if (onLongPressSelect) {
      onLongPressSelect(product.id);
    } else {
      setShowAddToCollection(true);
    }
    setTimeout(() => setIsLongPressing(false), 150);
  }, [onLongPressSelect, product.id]);

  const longPressHandlers = useLongPress({
    onLongPress: handleLongPressAction,
  });


  // Track dropdown open state to prevent card click-through when closing
  const menuOpenRef = useRef(false);

  const handleCardNavigation = (e: React.MouseEvent) => {
    // If the menu was just open, swallow this click to prevent navigation
    if (menuOpenRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (!selectionMode) return; // Let Link handle navigation
    e.preventDefault();
    e.stopPropagation();
    onToggleSelect?.(product.id);
    if (showTapHint) onTapHintDismiss?.();
  };

  // Source label - what shows below the image
  const sourceLabel = product.sourceType === 'image' 
    ? 'Saved'
    : socialPlatform 
    ? socialPlatform.charAt(0).toUpperCase() + socialPlatform.slice(1)
    : product.store || 'Saved';

  return (
    <>
      <Link
        to={linkTo}
        onClick={handleCardNavigation}
        {...(selectionMode ? {} : longPressHandlers)}
        className={cn(
          'block rounded-xl overflow-hidden bg-card border border-border/50 transition-all duration-150 touch-manipulation',
          isLongPressing && 'scale-[0.97] shadow-lg',
          isSelected && 'border-transparent ring-2 ring-primary shadow-md shadow-primary/20',
          selectionMode && 'cursor-pointer'
        )}
      >
        {/* Square image container */}
        <div className={cn(
          'relative aspect-square',
          isExtracting ? 'bg-white' : 'bg-surface-2'
        )}>
          {isExtracting ? (
            <FashionLoadingAnimation />
          ) : (
            <>
              <OptimizedImage
                src={product.image}
                alt={product.name}
                priority={priority}
                className="w-full h-full"
                objectPosition={product.focalPoint 
                  ? `${product.focalPoint.x}% ${product.focalPoint.y}%` 
                  : 'center'}
              />
              
              {/* Selection checkbox */}
              {selectionMode && (
                <div className="absolute top-2 left-2 z-10">
                  {isSelected ? (
                    <CheckCircle2 className="w-6 h-6 text-white fill-primary drop-shadow-lg" />
                  ) : (
                    <Circle className="w-6 h-6 text-white/80 drop-shadow-lg" />
                  )}
                </div>
              )}

              {/* Top-left badges: Purchased, Pinned, Reported */}
              {!selectionMode && (isPurchased || isPinned || isReported) && (
                <div className="absolute top-2 left-2 flex items-center gap-1.5">
                  {isPurchased && (
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/90 backdrop-blur-sm">
                      <Check className="w-3 h-3 text-white" />
                      <span className="text-[10px] font-medium text-white">Purchased</span>
                    </div>
                  )}
                  {isPinned && !isPurchased && (
                    <div className="w-6 h-6 rounded-full bg-primary/90 backdrop-blur-sm flex items-center justify-center">
                      <Pin className="w-3 h-3 text-white" />
                    </div>
                  )}
                  {isReported && !isPurchased && (
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/80 backdrop-blur-sm">
                      <Wrench className="w-3 h-3 text-white" />
                      <span className="text-[10px] font-medium text-white">Under review</span>
                    </div>
                  )}
                </div>
              )}

              {/* Try On button - top right (fashion items only) */}
              {!selectionMode && (
                <div className="absolute top-2 right-2 z-10">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (showTryOnNudge) onTryOnNudgeDismiss?.();
                      navigate('/try-on', { 
                        state: { 
                          productImage: product.image, 
                          productName: product.name,
                          productId: product.id,
                          productPrice: product.price,
                          productCurrency: product.currency,
                          productStore: product.store,
                          productUrl: product.url,
                        } 
                      });
                    }}
                    className={cn(
                      "w-8 h-8 rounded-full bg-black/40 backdrop-blur-md text-white border border-white/20 shadow-md flex items-center justify-center active:bg-black/60 active:scale-95 transition-all",
                      showTryOnNudge && "animate-pulse"
                    )}
                    aria-label="Try On"
                  >
                    <Shirt className="w-4 h-4" />
                  </button>
                  <TryOnNudgeTooltip
                    visible={showTryOnNudge}
                    onDismiss={() => onTryOnNudgeDismiss?.()}
                  />
                </div>
              )}

              {/* Price pill - bottom left */}
              {hasValidPrice && !isPurchased && !selectionMode && (
                <div className="absolute bottom-2 left-2 bg-background/90 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-sm leading-[115%]">
                  {ratesLoading ? (
                    <div className="h-3 w-10 bg-muted animate-pulse rounded" />
                  ) : (
                    <span className="text-xs font-semibold text-foreground">{formattedPrice}</span>
                  )}
                </div>
              )}

              {/* Collection indicator - bottom right */}
              {!selectionMode && isInCollection && (
                <div className="absolute bottom-2 right-2 w-5 h-5 rounded-full bg-primary/80 backdrop-blur-sm flex items-center justify-center">
                  <FolderCheck className="w-3 h-3 text-white" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Text content below image */}
        <div className="p-2.5 flex items-start justify-between gap-1">
          <div className="flex-1 min-w-0">
            {isExtracting ? (
              <div className="space-y-1.5">
                <div className="h-3 w-16 bg-muted animate-pulse rounded" />
                <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
              </div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground truncate">{sourceLabel}</p>
                <p className="text-sm font-medium text-foreground whitespace-nowrap overflow-hidden text-ellipsis">{product.name}</p>
              </>
            )}
          </div>

          {/* More menu */}
          {!selectionMode && !isExtracting && (
            <DropdownMenu onOpenChange={(open) => {
              // Use a microtask so the flag is cleared after the current click event finishes
              if (open) {
                menuOpenRef.current = true;
              } else {
                requestAnimationFrame(() => { menuOpenRef.current = false; });
              }
            }}>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  className="p-1 hover:bg-muted rounded-full transition-colors shrink-0 -mt-0.5"
                >
                  <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {/* — Organize — */}
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShowAddToCollection(true); }}>
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Add to Collection
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleTogglePin}>
                  <Pin className="w-4 h-4 mr-2" />
                  {isPinned ? 'Unpin' : 'Pin to Top'}
                </DropdownMenuItem>
                {product.isSavedItem && !isPurchased && (
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    if (!user) { toast.error('Please sign in'); return; }
                    markAsPurchased.mutate({
                      savedItemId: product.id,
                      purchasePrice: product.price,
                      currency: product.currency,
                      itemOwnerId: user.id,
                    }, {
                      onSuccess: () => toast.success('Added to Bought Items'),
                      onError: () => toast.error('Failed to mark as purchased'),
                    });
                  }}>
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Mark as Purchased
                  </DropdownMenuItem>
                )}

                {/* — Share & Social — */}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShowShareSheet(true); }}>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShowCreatePoll(true); }}>
                  <Vote className="w-4 h-4 mr-2" />
                  Get Opinions
                </DropdownMenuItem>
                {product.image && (
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    navigate('/chat', {
                      state: {
                        prefillImage: product.image,
                        productName: product.name,
                      },
                    });
                  }}>
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Chat about this
                  </DropdownMenuItem>
                )}

                {/* — Manage — */}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); setShowFixInfo(true); }}
                  disabled={isReported}
                >
                  {isReported ? <Wrench className="w-4 h-4 mr-2" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                  {isReported ? 'Under review' : 'Fix item info'}
                </DropdownMenuItem>
                {onDelete && (
                  <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </Link>

      <AddToCollectionSheet
        open={showAddToCollection}
        onOpenChange={setShowAddToCollection}
        savedItemId={product.id}
        itemName={product.name}
      />
      
      <ShareSheet
        open={showShareSheet}
        onOpenChange={setShowShareSheet}
        title={product.name}
        imageUrl={product.image}
        url={product.url}
        savedItemId={product.id}
      />

      <CreatePollSheet
        open={showCreatePoll}
        onOpenChange={setShowCreatePoll}
        initialImageUrl={product.image}
        initialImageName={product.name}
      />

      <FixItemInfoSheet
        open={showFixInfo}
        onOpenChange={setShowFixInfo}
        savedItemId={product.id}
      />
    </>
  );
});

export default SavesGridCard;
